"use client";

/**
 * useDiscoverySpotlights
 * @description 화면 아래를 채우는 목록의 상태 — 받아 둔 페이지들과 다음 페이지 불러오기.
 *   첫 페이지는 서버가 주고, 그 뒤는 서버 액션으로 이어 받는다.
 *
 *   **로딩이 빈 상태보다 먼저다.** 아직 오는 중인 것을 "없다"고 말하지 않는다.
 */
import { useCallback, useMemo, useState } from "react";

import type { SpotlightItem } from "./DiscoverySpotlights.types";
import { toSpotlightItem } from "./toSpotlightItem";

import { trackSpotlightsLoadMore } from "../../analytics";

import { loadBlogItems, type BlogListResponse } from "@/domains/core/post";

import { SPOTLIGHT_PAGE_SIZE } from "./DiscoverySpotlights.constants";

type Status = "idle" | "loading" | "error";

export const useDiscoverySpotlights = (
  initialPage: BlogListResponse | null,
) => {
  const [pages, setPages] = useState<BlogListResponse[]>(
    initialPage ? [initialPage] : [],
  );
  const [status, setStatus] = useState<Status>(initialPage ? "idle" : "error");

  const items = useMemo<SpotlightItem[]>(
    () => pages.flatMap((page) => page.data).map(toSpotlightItem),
    [pages],
  );

  const nextCursor = pages.at(-1)?.nextCursor ?? undefined;
  const hasNextPage = pages.length === 0 || Boolean(nextCursor);

  const loadMore = useCallback(async () => {
    if (status === "loading") return;
    if (pages.length > 0 && !nextCursor) return;
    trackSpotlightsLoadMore(pages.length);
    setStatus("loading");
    try {
      const page = await loadBlogItems(SPOTLIGHT_PAGE_SIZE, nextCursor);
      setPages((prev) => [...prev, page]);
      setStatus("idle");
    } catch {
      setStatus("error");
    }
  }, [nextCursor, pages.length, status]);

  return {
    items,
    hasNextPage,
    isLoading: status === "loading",
    isError: status === "error",
    loadMore,
  };
};
